import { makeAutoObservable, runInAction } from 'mobx';

import type { CreatePay4MeCampaignUseCase } from '@/core/application/use-cases/pay4me/CreatePay4MeCampaignUseCase';
import type {
  Pay4MeCampaignDetailsResponse,
  Pay4MeCampaignResponse,
  Pay4MeContributionRequest,
  Pay4MeContributionResponse,
  Pay4MeOrderContext,
  Pay4MePSSImpactResponse,
  Pay4MeService,
} from '@/core/infrastructure/services/Pay4MeService';
import { normalizeMoneyString } from '@/shared/utils/price';

export interface CampaignFormData {
  title: string;
  description: string;
  durationDays: number;
}

type CampaignBalancePlanPayload = {
  plan_kind?: string | null;
  amount?: unknown;
  total_amount?: unknown;
  amount_left?: unknown;
  remaining_balance?: unknown;
  ui_capabilities?: {
    hide_pss_affordances?: boolean | null;
  } | null;
};

type CampaignOrderPayload = {
  id?: string;
  order_number?: string;
  total_amount?: unknown;
  remaining_balance?: unknown;
  currency?: string | null;
  payment_method?: string | null;
  payment_plan_id?: string | null;
  items?: Array<{
    product_name?: string | null;
    product_image?: string | null;
    quantity?: number | null;
  }>;
  exchange?: {
    settlement_mode?: string | null;
    balance_plan?: CampaignBalancePlanPayload | null;
  } | null;
};

type CampaignPaymentPlanPayload = CampaignBalancePlanPayload & {
  installments?: Array<{ due_date?: string | null }>;
};

function isInstallmentBalance(settlementMode?: string | null): boolean {
  return String(settlementMode || '').toUpperCase() === 'INSTALLMENT_BALANCE';
}

function isExchangeBalancePlan(plan?: CampaignBalancePlanPayload | null): boolean {
  return (
    plan?.plan_kind === 'exchange_balance' ||
    plan?.ui_capabilities?.hide_pss_affordances === true
  );
}

function isDetailedExchangeBalancePlan(plan?: CampaignBalancePlanPayload | null): boolean {
  if (!isExchangeBalancePlan(plan)) return false;
  return [
    plan?.total_amount,
    plan?.amount,
    plan?.amount_left,
    plan?.remaining_balance,
  ].some((value) => value !== undefined && value !== null);
}

function resolveCampaignDisplayAmounts(
  order: CampaignOrderPayload,
  paymentPlan: CampaignPaymentPlanPayload | null,
): { totalAmount: string; remainingBalance?: string } {
  const exchangePlan = order.exchange?.balance_plan ?? null;
  const exchangeBalanceBySettlement = isInstallmentBalance(order.exchange?.settlement_mode);
  const fetchedExchangePlan = isExchangeBalancePlan(paymentPlan) ? paymentPlan : null;
  const embeddedExchangePlan = isDetailedExchangeBalancePlan(exchangePlan) ? exchangePlan : null;
  const plan = fetchedExchangePlan ?? (exchangeBalanceBySettlement ? paymentPlan : null) ?? embeddedExchangePlan;
  const isExchangeBalance =
    exchangeBalanceBySettlement ||
    isExchangeBalancePlan(plan) ||
    isExchangeBalancePlan(exchangePlan);

  if (isExchangeBalance) {
    const totalAmount =
      normalizeMoneyString(plan?.total_amount) ??
      normalizeMoneyString(plan?.amount) ??
      normalizeMoneyString(order.remaining_balance) ??
      normalizeMoneyString(order.total_amount) ??
      '0.00';
    return {
      totalAmount,
      remainingBalance:
        normalizeMoneyString(plan?.amount_left) ??
        normalizeMoneyString(plan?.remaining_balance) ??
        normalizeMoneyString(order.remaining_balance) ??
        totalAmount,
    };
  }

  return {
    totalAmount: normalizeMoneyString(order.total_amount) ?? '0.00',
    remainingBalance: normalizeMoneyString(order.remaining_balance) ?? undefined,
  };
}

export class Pay4MeStore {
  campaigns: Pay4MeCampaignDetailsResponse[] = [];
  currentCampaign: Pay4MeCampaignDetailsResponse | null = null;
  campaignCreationResult: Pay4MeCampaignResponse | null = null;
  pssImpact: Pay4MePSSImpactResponse | null = null;
  contributionResult: Pay4MeContributionResponse | null = null;

  // Campaign creation flow state
  campaignFormData: CampaignFormData | null = null;
  orderContext: Pay4MeOrderContext | null = null;
  isLoadingOrder = false;

  isLoading = false;
  isCreating = false;
  isExtending = false;
  isContributing = false;
  error: string | null = null;

  constructor(
    private readonly createPay4MeCampaignUseCase: CreatePay4MeCampaignUseCase,
    private readonly pay4meService: Pay4MeService
  ) {
    makeAutoObservable(this);
  }

  get hasCampaign(): boolean {
    return this.currentCampaign !== null;
  }

  get campaignProgress(): number {
    return this.currentCampaign?.progress?.progressPercentage ?? 0;
  }

  get remainingAmount(): string {
    return this.currentCampaign?.progress?.remainingBalance ?? '0';
  }

  get canAcceptContributions(): boolean {
    if (!this.currentCampaign) return false;
    const status = (this.currentCampaign.status || '').toLowerCase();
    if (status !== 'active') return false;
    if (this.isExpired) return false;
    return this.campaignProgress < 100;
  }

  get isExpired(): boolean {
    const expiresAt = this.currentCampaign?.expiresAt;
    if (!expiresAt) return false;
    return new Date(expiresAt).getTime() < Date.now();
  }

  /**
   * Get days until campaign expires (negative if already expired)
   */
  get daysUntilExpiration(): number | null {
    const expiresAt = this.currentCampaign?.expiresAt;
    if (!expiresAt) return null;

    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }

  /**
   * Get human-readable expiration status for UI display
   */
  get expirationStatus(): 'active' | 'expiring_soon' | 'expired' {
    const days = this.daysUntilExpiration;
    if (days === null) return 'active';
    if (days <= 0) return 'expired';
    if (days <= 3) return 'expiring_soon';
    return 'active';
  }

  /**
   * Get reason why contributions are blocked (if any)
   * Returns null if contributions are allowed
   */
  get contributionBlockedReason(): string | null {
    if (!this.currentCampaign) return 'Campaign not found';
    if (this.isExpired) return 'Campaign has expired';
    if (this.campaignProgress >= 100) return 'Goal already reached';
    const status = (this.currentCampaign.status || '').toLowerCase();
    if (status !== 'active') return `Campaign is ${status}`;
    return null;
  }

  /**
   * Check if campaign can be extended (owner only action)
   * Can extend if: campaign exists AND (expired OR expiring soon OR user wants more time)
   */
  get canExtendCampaign(): boolean {
    if (!this.currentCampaign) return false;
    // Can always extend if goal not yet reached
    return this.campaignProgress < 100;
  }

  async createCampaign(orderId: string, formData: CampaignFormData, userId: string): Promise<void> {
    this.setCreating(true);
    this.setError(null);

    try {
      const result = await this.createPay4MeCampaignUseCase.execute({
        orderId,
        title: formData.title,
        description: formData.description,
        durationDays: formData.durationDays,
        userId,
      });

      runInAction(() => {
        this.campaignCreationResult = result;
      });
    } catch (error) {
      this.setError(error instanceof Error ? error.message : 'Failed to create campaign');
      throw error;
    } finally {
      this.setCreating(false);
    }
  }

  /**
   * Extend an existing campaign's duration
   * Uses the same POST endpoint which handles extend/renew automatically
   *
   * @param orderId - The order ID with existing campaign
   * @param additionalDays - Days to add to the campaign
   */
  async extendCampaign(orderId: string, additionalDays: number): Promise<void> {
    this.setExtending(true);
    this.setError(null);

    try {
      const result = await this.pay4meService.extendCampaign(orderId, additionalDays);
      runInAction(() => {
        this.campaignCreationResult = result;
        // Also update current campaign's expiration if loaded
        if (this.currentCampaign && result.expiresAt) {
          this.currentCampaign.expiresAt = result.expiresAt;
        }
      });
    } catch (error) {
      this.setError(error instanceof Error ? error.message : 'Failed to extend campaign');
      throw error;
    } finally {
      this.setExtending(false);
    }
  }

  async loadCampaignDetails(campaignId?: string, sharingToken?: string): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const campaign = await this.pay4meService.getCampaignDetails(campaignId, sharingToken);
      runInAction(() => {
        this.currentCampaign = campaign;
      });
    } catch (error) {
      this.setError(error instanceof Error ? error.message : 'Failed to load campaign');
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async contributeToCurrentCampaign(request: Pay4MeContributionRequest): Promise<void> {
    if (!this.currentCampaign) {
      throw new Error('No campaign loaded');
    }

    this.setContributing(true);
    this.setError(null);

    try {
      const result = await this.pay4meService.contributeToCampaign(this.currentCampaign.campaignId, request);
      runInAction(() => {
        this.contributionResult = result;
        if (this.currentCampaign && result.campaignProgress) {
          this.currentCampaign.progress = result.campaignProgress;
        }
      });
    } catch (error) {
      this.setError(error instanceof Error ? error.message : 'Failed to process contribution');
      throw error;
    } finally {
      this.setContributing(false);
    }
  }

  async recordShareEvent(
    sharingToken: string,
    payload: {
      channel: string;
      metadata?: Record<string, string | number | boolean>;
    }
  ): Promise<void> {
    await this.pay4meService.recordShareEvent(sharingToken, payload);
  }

  async loadPSSImpact(campaignId: string): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const impact = await this.pay4meService.getPSSImpact(campaignId);
      runInAction(() => {
        this.pssImpact = impact;
      });
    } catch (error) {
      this.setError(error instanceof Error ? error.message : 'Failed to load PSS impact');
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  clearCampaignCreationResult(): void {
    this.campaignCreationResult = null;
  }

  clearContributionResult(): void {
    this.contributionResult = null;
  }

  clearCurrentCampaign(): void {
    this.currentCampaign = null;
    this.pssImpact = null;
  }

  /**
   * Set form data for campaign creation flow
   */
  setCampaignFormData(data: Partial<CampaignFormData>): void {
    runInAction(() => {
      this.campaignFormData = {
        title: data.title ?? this.campaignFormData?.title ?? '',
        description: data.description ?? this.campaignFormData?.description ?? '',
        durationDays: data.durationDays ?? this.campaignFormData?.durationDays ?? 7,
      };
    });
  }

  /**
   * Load order context for campaign creation display
   * Also fetches payment plan to get end date for duration constraints
   */
  async loadOrderForCampaign(orderId: string): Promise<void> {
    runInAction(() => {
      this.isLoadingOrder = true;
      this.error = null;
    });

    try {
      const response = await fetch(`/api/proxy/v1/orders/${orderId}`, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error('Failed to load order');
      }
      const order = await response.json() as CampaignOrderPayload;

      // Extract first item for product display
      const primaryItem = order.items?.[0];

      // Fetch payment plan to get end date (last installment due date)
      let paymentPlanEndDate: string | null = null;
      let paymentPlan: CampaignPaymentPlanPayload | null = null;
      if (order.payment_plan_id) {
        try {
          const planResponse = await fetch(`/api/proxy/v1/payment-plans/${order.payment_plan_id}`, { cache: 'no-store' });
          if (planResponse.ok) {
            const plan = await planResponse.json() as CampaignPaymentPlanPayload;
            paymentPlan = plan;
            // Get end date from last installment's due_date
            if (plan.installments && plan.installments.length > 0) {
              const lastInstallment = plan.installments[plan.installments.length - 1];
              paymentPlanEndDate = lastInstallment?.due_date ?? null;
            }
          }
        } catch {
          // Non-critical - continue without plan end date
          console.warn('Failed to fetch payment plan for end date');
        }
      }
      const displayAmounts = resolveCampaignDisplayAmounts(order, paymentPlan);

      runInAction(() => {
        this.orderContext = {
          orderId: order.id ?? orderId,
          orderNumber: order.order_number,
          productName: primaryItem?.product_name ?? '',
          productImageUrl: primaryItem?.product_image ?? null,
          quantity: primaryItem?.quantity ?? null,
          totalAmount: displayAmounts.totalAmount,
          currency: order.currency ?? 'GHS',
          paymentMethod: order.payment_method ?? undefined,
          remainingBalance: displayAmounts.remainingBalance,
          paymentPlanEndDate,
        };
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to load order';
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoadingOrder = false;
      });
    }
  }

  /**
   * Clear all campaign creation flow state
   */
  clearCampaignFlow(): void {
    runInAction(() => {
      this.campaignFormData = null;
      this.campaignCreationResult = null;
      this.orderContext = null;
      this.error = null;
    });
  }

  /**
   * Buffer days between campaign expiry and payment plan end
   * Reduced from 7 to 3 days based on evidence-driven approach
   */
  private static readonly CAMPAIGN_BUFFER_DAYS = 3;

  /**
   * Calculate maximum allowed campaign duration (business constraint)
   * This is the CAP - users cannot exceed this regardless of preference
   *
   * Logic:
   * - Absolute max: 90 days
   * - If payment plan exists: min(90, remaining_plan_days - buffer)
   * - Buffer ensures campaign ends before payment plan
   */
  calculateMaxAllowedDuration(planEndDate?: Date | string | null): number {
    const ABSOLUTE_MAX = 90;

    if (!planEndDate) {
      return ABSOLUTE_MAX;
    }

    const endDate = typeof planEndDate === 'string' ? new Date(planEndDate) : planEndDate;
    const now = new Date();
    const remainingPlanDays = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    // Cap at remaining plan days minus buffer, minimum 1 day
    return Math.min(ABSOLUTE_MAX, Math.max(1, remainingPlanDays - Pay4MeStore.CAMPAIGN_BUFFER_DAYS));
  }

  /**
   * Calculate smart default duration (conversion optimization)
   * This is the DEFAULT - what we suggest for best conversion
   *
   * Hybrid Algorithm:
   * 1. Time-based: 30/45/60/75/90 days based on remaining months in plan
   *    - Aligns with payday cycles (contributors get paid monthly)
   * 2. Edge case: If <25% balance remaining, use 14 days (quick win)
   *
   * The default is then capped by calculateMaxAllowedDuration()
   */
  calculateDefaultDuration(
    remainingBalance: number,
    totalAmount: number,
    planEndDate?: Date | string | null
  ): number {
    const maxAllowed = this.calculateMaxAllowedDuration(planEndDate);

    // Edge case: Almost done paying - short campaign
    if (totalAmount > 0) {
      const remainingPercent = (remainingBalance / totalAmount) * 100;
      if (remainingPercent < 25) {
        return Math.min(14, maxAllowed);
      }
    }

    // Time-based algorithm: Align with payday cycles
    // 30 days = 1 payday, 45 = 1.5, 60 = 2, etc.
    let suggestedDuration = 30; // Base: 1 month minimum

    if (planEndDate) {
      const endDate = typeof planEndDate === 'string' ? new Date(planEndDate) : planEndDate;
      const now = new Date();
      const remainingPlanDays = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      const remainingMonths = Math.ceil(remainingPlanDays / 30);

      // 30 base + 15 per additional month (30/45/60/75/90)
      if (remainingMonths >= 4) {
        suggestedDuration = 90;
      } else if (remainingMonths >= 3) {
        suggestedDuration = 75;
      } else if (remainingMonths >= 2) {
        suggestedDuration = 60;
      } else if (remainingMonths >= 1.5) {
        suggestedDuration = 45;
      } else {
        suggestedDuration = 30;
      }
    }

    return Math.min(suggestedDuration, maxAllowed);
  }

  /**
   * Get available duration options based on payment plan end date
   * Returns standard options filtered by max allowed duration
   */
  getDurationOptions(planEndDate?: Date | string | null): Array<{ value: number; label: string }> {
    // Updated options to match hybrid algorithm (30/45/60/75/90)
    // Kept 7 and 14 for edge cases (short plans, almost-paid)
    const standardOptions = [
      { value: 7, label: '7 days' },
      { value: 14, label: '14 days' },
      { value: 30, label: '30 days' },
      { value: 45, label: '45 days' },
      { value: 60, label: '60 days' },
      { value: 75, label: '75 days' },
      { value: 90, label: '90 days' },
    ];

    // Get max allowed using the centralized calculation
    const maxAllowedDays = this.calculateMaxAllowedDuration(planEndDate);

    // Filter options that fit within max allowed
    let availableOptions = standardOptions.filter(opt => opt.value <= maxAllowedDays);

    // Edge case: If no standard options fit, add a custom one
    if (availableOptions.length === 0 && maxAllowedDays >= 1) {
      availableOptions = [{ value: maxAllowedDays, label: `${maxAllowedDays} day${maxAllowedDays > 1 ? 's' : ''}` }];
    }

    // If there's a significant gap, add "max available" option
    if (availableOptions.length > 0 && maxAllowedDays > 7) {
      const lastOption = availableOptions[availableOptions.length - 1];
      if (lastOption && maxAllowedDays - lastOption.value >= 10) {
        availableOptions.push({
          value: maxAllowedDays,
          label: `${maxAllowedDays} days (max)`
        });
      }
    }

    return availableOptions;
  }

  validateCampaignForm(formData: CampaignFormData): string[] {
    const errors: string[] = [];

    // Title validation: optional but if provided, 5-160 chars (backend limit)
    if (formData.title && formData.title.trim().length > 0 && formData.title.trim().length < 5) {
      errors.push('Title must be at least 5 characters long');
    }
    if (formData.title.length > 160) {
      errors.push('Title cannot exceed 160 characters');
    }

    // Description validation: optional but if provided, 10-280 chars (backend limit)
    if (formData.description && formData.description.trim().length > 0 && formData.description.trim().length < 10) {
      errors.push('Description must be at least 10 characters long');
    }
    if (formData.description.length > 280) {
      errors.push('Description cannot exceed 280 characters');
    }

    // Duration validation: 1-90 days
    if (formData.durationDays < 1 || formData.durationDays > 90) {
      errors.push('Duration must be between 1 and 90 days');
    }

    return errors;
  }

  validateContribution(amount: number): string[] {
    const errors: string[] = [];
    if (!this.currentCampaign) {
      errors.push('No campaign selected');
      return errors;
    }

    const rules = this.currentCampaign.campaignRules;
    if (!rules) {
      errors.push('Campaign rules unavailable');
      return errors;
    }

    const minContribution = Number(rules.minContribution);
    const maxContribution = Number(rules.maxContribution);

    if (Number.isFinite(minContribution) && amount < minContribution) {
      errors.push(`Minimum contribution is GHS ${minContribution}`);
    }
    if (Number.isFinite(maxContribution) && amount > maxContribution) {
      errors.push(`Maximum contribution is GHS ${maxContribution}`);
    }
    if (!this.canAcceptContributions) {
      errors.push('Campaign is not accepting contributions');
    }

    return errors;
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  private setCreating(creating: boolean): void {
    this.isCreating = creating;
  }

  private setExtending(extending: boolean): void {
    this.isExtending = extending;
  }

  private setContributing(contributing: boolean): void {
    this.isContributing = contributing;
  }

  private setError(message: string | null): void {
    this.error = message;
  }
}
